if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 1025px)');

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Listen to scroll events on mobile to sync active thumbnail
        const sliderList = this.elements.viewer.querySelector('.product__media-list');
        if (sliderList) {
          sliderList.addEventListener('scroll', debounce(() => {
            if (window.innerWidth <= 1024) {
              const scrollLeft = sliderList.scrollLeft;
              const slideWidth = sliderList.clientWidth;
              const activeIndex = Math.round(scrollLeft / slideWidth);
              const slides = sliderList.querySelectorAll('[data-media-id]');
              if (slides[activeIndex]) {
                const mediaId = slides[activeIndex].dataset.mediaId;
                const activeThumb = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
                if (activeThumb) this.setActiveThumbnail(activeThumb);
              }
            }
          }, 150), { passive: true });
        }
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.addEventListener('click', (e) => {
            e.preventDefault();
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
        });
      }

      this.initVerticalThumbnails();
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    initVerticalThumbnails() {
      const container = this.elements?.thumbnails;
      if (!container) return;

      const list = container.querySelector('.thumbnail-list');
      const prevBtn = container.querySelector('.slider-button--prev');
      const nextBtn = container.querySelector('.slider-button--next');

      if (!list) return;

      const updateButtonStates = () => {
        if (!prevBtn || !nextBtn) return;
        if (window.innerWidth <= 1024) {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
          return;
        }
        const maxScroll = list.scrollHeight - list.clientHeight;
        if (maxScroll <= 5) {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
          return;
        }
        prevBtn.style.display = 'flex';
        nextBtn.style.display = 'flex';
        prevBtn.disabled = list.scrollTop <= 2;
        nextBtn.disabled = list.scrollTop >= maxScroll - 2;
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          list.scrollBy({ top: -108, behavior: 'smooth' });
          setTimeout(updateButtonStates, 300);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          list.scrollBy({ top: 108, behavior: 'smooth' });
          setTimeout(updateButtonStates, 300);
        });
      }

      list.addEventListener('scroll', updateButtonStates, { passive: true });
      window.addEventListener('resize', updateButtonStates, { passive: true });
      setTimeout(updateButtonStates, 100);
    }

    onSlideChanged(event) {
      if (!this.elements?.thumbnails || !event.detail?.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (!this.elements?.viewer) {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
        };
      }
      if (!this.elements?.viewer) return;

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      const isDesktop = window.innerWidth > 1024;
      if (isDesktop) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.style.display = (element === activeMedia) ? 'block' : 'none';
        });
      } else {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.style.display = 'block';
        });
        const sliderList = this.elements.viewer.querySelector('.product__media-list');
        if (sliderList) {
          sliderList.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: 'smooth'
          });
        }
      }

      this.preventStickyHeader();
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail.dataset.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements?.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', 'true');

      if (window.innerWidth > 1024) {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        thumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      if (!activeItem) return;
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) {
        if (!deferredMedia.getAttribute('loaded')) {
          deferredMedia.loadContent(false);
        } else {
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.muted = true;
            video.playsInline = true;
            video.currentTime = 0;
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
          }
        }
      }
      const directVideo = activeItem.querySelector('video');
      if (directVideo) {
        directVideo.muted = true;
        directVideo.playsInline = true;
        directVideo.currentTime = 0;
        const playPromise = directVideo.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    }

    preventStickyHeader() {
      const stickyHeader = document.querySelector('sticky-header');
      if (!stickyHeader) return;
      stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements?.viewer?.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });

  // Global event delegation fallback for thumbnail clicks
  document.addEventListener('click', (e) => {
    const thumbItem = e.target.closest('.thumbnail-list__item[data-target]');
    if (thumbItem) {
      e.preventDefault();
      const mg = document.querySelector('media-gallery');
      if (mg && typeof mg.setActiveMedia === 'function') {
        mg.setActiveMedia(thumbItem.dataset.target, false);
      }
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const mg = document.querySelector('media-gallery');
    if (mg && typeof mg.init === 'function') {
      mg.init();
    }
  });
}
